import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Location } from '@angular/common';
import { SearchService } from '../../services/search.service';
import { ExportService } from '../../services/export.service';
import { FormCompanyService } from '../../services/forms/form-company.service';
import { Subscription } from 'rxjs';
import { ModulesService } from 'src/app/services/modules.service';
import { AuthService } from 'src/app/services/auth.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MENU_V2, generateModuleMenuItems } from '../../menu-v2.config';
import { ICONS } from '../../icons';


type HeaderButtonAction = 'create' | 'back' | 'sendForm';

interface HeaderButton {
  label: string;
  action: HeaderButtonAction;
}

@Component({
  selector: 'app-header-v2',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.sass']
})
export class HeaderComponent implements OnInit, OnDestroy {

  breadcrumbs: string[] = [];
  headerButton: HeaderButton | null = null;
  showSearch = false;
  showMobileSearch = false;
  searchInputValue = '';
  showExportButton = false;
  showSearchField = true;
  isExporting = false;
  showSaveButton = false;
  private permission = '';
  selectedIconName?: keyof typeof ICONS;
  selectedIconSvg: SafeHtml | null = null;

  private exportingSubscription?: Subscription;

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('mobileSearchInput') mobileSearchInput!: ElementRef<HTMLInputElement>;

  @Output() searchChange = new EventEmitter<string>();
  @Output() exportCsv = new EventEmitter<void>();
  @Output() openMobileSidebar = new EventEmitter<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private searchService: SearchService,
    private exportService: ExportService,
    private formCompanyService: FormCompanyService,
    private modulesService: ModulesService,
    private authService: AuthService,
    private sanitizer: DomSanitizer
  ) {}

  onOpenMobileSidebar(): void {
    this.openMobileSidebar.emit();
  }

  ngOnInit(): void {
    this.permission = this.authService.getPermission();
    this.updateHeader();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateHeader();
        // Limpa o campo de busca ao navegar
        this.searchInputValue = '';
        this.showMobileSearch = false;
      });

    // Escuta mudanças no estado de exportação
    this.exportingSubscription = this.exportService.exportingState$.subscribe((value: boolean) => {
      this.isExporting = value;
    });

    // Escuta mudanças no termo de busca vindas de outros componentes
    this.searchService.searchTermImmediate$.subscribe((term: string) => {
      if (this.searchInputValue !== term) {
        this.searchInputValue = term;
      }
    });

    // Escuta quando o campo de busca deve ser aberto programaticamente
    this.searchService.searchOpen$.subscribe((open: boolean) => {
      if (open && !this.showSearch) {
        this.showSearch = true;
        setTimeout(() => {
          if (this.searchInput) {
            this.searchInput.nativeElement.focus();
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.exportingSubscription?.unsubscribe();
  }

  private updateHeader(): void {
    this.breadcrumbs = [];
    this.headerButton = null;
    this.showExportButton = false;
    this.showSearchField = true;
    this.showSaveButton = false;

    this.buildFromRoute(this.route.root);
    
    // Verifica se está na tela de pacientes ou dashboard para mostrar botão de exportar
    const currentUrl = this.router.url;
    if (currentUrl === '/main/v2/patients' || currentUrl.includes('/dashboard')) {
      this.showExportButton = true;
    }
    
    // Esconde campo de pesquisa nas telas de prestadores e usuários
    if (currentUrl.includes('/providers') || currentUrl.includes('/users')) {
      this.showSearchField = false;
    }

    // Esconde campo de pesquisa em todas as sub-rotas de pacientes (exceto a lista principal)
    const isPatientSubRoute = /\/main\/v2\/patients\/.+/.test(currentUrl);
    if (isPatientSubRoute) {
      this.showSearchField = false;
    }

    // Esconde campo de pesquisa nas telas de anamnese/formulário/assinaturas (lista/visão)
    // Detecta pela URL ou pelo breadcrumb gerado (contendo 'Anamnese', 'Formulário' ou 'Assinatura')
    const breadcrumbText = this.breadcrumbs.join(' ').toLowerCase();
    if (
      currentUrl.includes('/anamnese') ||
      currentUrl.includes('/anamneses') ||
      currentUrl.includes('/signatures') ||
      currentUrl.includes('/dashboard') ||
      breadcrumbText.includes('anamnese') ||
      breadcrumbText.includes('formulário') ||
      breadcrumbText.includes('formulario') ||
      breadcrumbText.includes('assinatura') ||
      breadcrumbText.includes('assinaturas')
      || breadcrumbText.includes('dashboard')
    ) {
      this.showSearchField = false;
    }

    // Esconde campo de pesquisa nas sub-rotas de templates (create/edit), mas mantém na lista
    const isTemplateSubRoute = /\/main\/v2\/templates\/.+/.test(currentUrl);
    if (isTemplateSubRoute) {
      this.showSearchField = false;
    }
    
    // Se está na tela de empresa e não é ROOT, esconde o botão de criar
    if (currentUrl.includes('/company') && this.permission !== 'ROOT') {
      // Não mostra botão de criar empresa para não-ROOT
      const btn = this.headerButton as HeaderButton | null;
      if (btn && btn.action === 'create') {
        this.headerButton = null;
      }
    }

    // Se está na tela de prestadores e não é ROOT, esconde o botão de criar
    if (currentUrl.includes('/providers') && this.permission !== 'ROOT') {
      const btn = this.headerButton as HeaderButton | null;
      if (btn && btn.action === 'create') {
        this.headerButton = null;
      }
    }

    // Nas rotas de empresa, somente usuários ROOT devem ver o campo de pesquisa
    if (currentUrl.includes('/company')) {
      this.showSearchField = this.permission === 'ROOT';
    }
    
    // Detecta se está no modo de edição de empresa (URL contém /company/{id}/edit)
    const isCompanyEditMode = /\/company\/[^\/]+\/edit/.test(currentUrl);
    const isCompanyCreateMode = currentUrl.includes('/company/create');
    
    // Esconde campo de pesquisa nas telas de criação e edição de empresa
    if (isCompanyCreateMode || isCompanyEditMode) {
      this.showSearchField = false;
      
      // No modo de edição, mostra o botão de Salvar
      if (isCompanyEditMode) {
        this.showSaveButton = true;
      }
    }

    // Atualiza o ícone mostrado no header para coincidir com o item selecionado do sidebar
    this.updateSelectedIcon(currentUrl);
  }

  private updateSelectedIcon(currentUrl: string): void {
    try {
      // Constrói uma cópia do menu base e injeta módulos dinâmicos (mesma lógica do sidebar)
      const menu = JSON.parse(JSON.stringify(MENU_V2)) as any[];

      const operationSection = menu.find((s: any) => s.title === 'operação');
      if (operationSection) {
        operationSection.items = operationSection.items.filter((it: any) => !it.isDynamicModule);
        // Gera itens dinâmicos com base no módulo atualmente selecionado
        const moduleName = this.modulesService.moduleName || '';
        if (moduleName && moduleName.trim() !== '') {
          const generated = generateModuleMenuItems(moduleName);
          operationSection.items.push(...generated);
        }
      }

      // Achata todos os itens e tenta encontrar um que corresponda à rota atual
      const flatItems: { route: string; icon: keyof typeof ICONS }[] = [];
      menu.forEach((section: any) => {
        (section.items || []).forEach((it: any) => {
          if (it && it.route) {
            flatItems.push({ route: it.route, icon: it.icon });
          }
        });
      });

      // Procura item com rota que cashe no início da URL atual (para rotas filhas)
      const found = flatItems.find(i => i.route && currentUrl.startsWith(i.route));

      if (found) {
        this.selectedIconName = found.icon as keyof typeof ICONS;
        const raw = ICONS[this.selectedIconName] || '';
        this.selectedIconSvg = this.sanitizer.bypassSecurityTrustHtml(raw);
      } else {
        // fallback: limpa ícone
        this.selectedIconName = undefined;
        this.selectedIconSvg = null;
      }
    } catch (e) {
      // noop - em caso de qualquer problema, não bloqueia header
      this.selectedIconName = undefined;
      this.selectedIconSvg = null;
    }
  }

  private buildFromRoute(route: ActivatedRoute): void {
    const data = route.snapshot.data;

    let breadcrumb = data['breadcrumb'] as string | undefined;
    const button = data['button'] as HeaderButton | undefined;

    // Substituir "Módulo" pelo nome real do módulo selecionado
    if (breadcrumb === 'Módulo') {
      breadcrumb = this.modulesService.moduleNamePascalCase || breadcrumb;
    }

    // Substituir referências de "Anamnese" por "Formulário" quando não for PACIENTES
    if (breadcrumb && this.modulesService.moduleName !== 'PACIENTES') {
      breadcrumb = this.replaceAnamneseWithFormulario(breadcrumb);
    }

    if (breadcrumb && this.breadcrumbs[this.breadcrumbs.length - 1] !== breadcrumb) {
      this.breadcrumbs.push(breadcrumb);
    }

    // sempre sobrescreve se existir (filha > pai)
    if ('button' in data) {
      this.headerButton = button || null;
    }

    // Verifica se está em uma rota filha de 'create'
    const urlSegments = route.snapshot.url.map(segment => segment.path);
    if (urlSegments.includes('create')) {
      this.headerButton = { label: 'Voltar', action: 'back' };
    } else if (route.snapshot.routeConfig?.path === 'main/v2/company' && this.permission === 'ROOT') {
      this.headerButton = { label: 'Criar', action: 'create' };
    }

    if (route.firstChild) {
      this.buildFromRoute(route.firstChild);
    }
  }

  onHeaderButtonClick(): void {
  if (!this.headerButton) return;

  switch (this.headerButton.action) {
    case 'back':
      this.location.back();
      break;

    case 'sendForm':
      // Navega para a lista de registros do módulo para selecionar o destinatário
      const moduleName = (this.modulesService.moduleName || '').toString().toUpperCase();
      if (moduleName === 'PACIENTES') {
        this.router.navigate(['main', 'v2', 'patients'], { queryParams: { headerSelectedOption: 'Anamnese' } });
      } else {
        this.router.navigate(['main', 'v2', 'modules', moduleName.toLowerCase()], { queryParams: { headerSelectedOption: 'Anamnese' } });
      }
      break;

    case 'create':
      // Navega para a rota de criação baseada na URL atual
      const currentUrl = this.router.url;
      if (currentUrl.includes('/users')) {
        this.router.navigate(['main/v2/users/create']);
      } else if (currentUrl.includes('/providers')) {
        this.router.navigate(['main/v2/providers/create']);
      } else if (currentUrl.includes('/procedures')) {
        this.router.navigate(['main/v2/procedures/create']);
      } else if (currentUrl.includes('/patients')) {
        this.router.navigate(['main/v2/patients/create']);
      } else if (currentUrl.includes('/company')) {
        // Clear company form data before navigating to create
        this.formCompanyService.clear();
        this.router.navigate(['main/v2/company/create']);
      } else if (currentUrl.includes('/modules/')) {
        // Rota dinâmica de módulos
        const moduleMatch = currentUrl.match(/\/modules\/([^\/]+)/);
        if (moduleMatch) {
          this.router.navigate(['main', 'v2', 'modules', moduleMatch[1], 'create']);
        }
      } else {
        // Fallback: adiciona /create na rota atual
        this.router.navigate([currentUrl + '/create']);
      }
      break;
  }
}

  toggleSearch(): void {
    if (this.showSearch && this.searchInputValue) {
      // Keep open if there's text — just refocus
      this.searchInput?.nativeElement.focus();
      return;
    }
    this.showSearch = !this.showSearch;
    if (this.showSearch && this.searchInput) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      });
    }
  }

  toggleMobileSearch(): void {
    this.showMobileSearch = !this.showMobileSearch;
    if (this.showMobileSearch) {
      setTimeout(() => {
        if (this.mobileSearchInput) {
          this.mobileSearchInput.nativeElement.focus();
        }
      });
    }
  }

  closeMobileSearch(): void {
    this.showMobileSearch = false;
    this.searchInputValue = '';
    this.searchService.setSearchTerm('');
    this.searchChange.emit('');
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchService.setSearchTerm(input.value);
    this.searchChange.emit(input.value);
  }

  onExportClick(): void {
    this.exportService.triggerExport();
  }

  onSaveClick(): void {
    this.formCompanyService.triggerSave();
  }

  setExporting(value: boolean): void {
    this.isExporting = value;
  }

  // Método para substituir referências de "Anamnese" por "Formulário" 
  // quando o módulo não for PACIENTES
  private replaceAnamneseWithFormulario(text: string): string {
    return text
      .replace(/Anamnese Enviada/g, 'Formulário Enviado')
      .replace(/Nova Anamnese/g, 'Novo Formulário')
      .replace(/Ficha de Anamnese/g, 'Ficha do Formulário')
      .replace(/Respostas da Anamnese/g, 'Respostas do Formulário')
      .replace(/Anamnese/g, 'Formulário')
      .replace(/anamnese/g, 'formulário');
  }
}
